/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Component imports
import { Button, Col, Form, Input, Row, Select } from 'antd';

// Misc imports
import { ACTIVITY_TYPES } from '../../constants/main';
import { generateFlowGraphChaptersource } from '../../utils/misc/generateFlowGraph';
import { useCreateActivityMutation } from '../../store/activities/activitiesApi';
import { IS_USER_FEEDBACK_ENABLED } from '../../constants/env';
import { useBotLanguage } from '../../utils/hooks/useBotLanguage';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import { INTEGRATION_CATEGORY } from '../../constants/integrations';

// Constant declarations
const { Option } = Select;

const ActivityForm = ({
  onSuccess
}) => {
  const [form] = Form.useForm();
  const { tenantId, orgId } = useParams();
  const { t } = useTranslation();
  const { primaryBotLanguage } = useBotLanguage();

  const { data: bot } = getCurrentQueryState('fetchBot', { orgId, tenantId });
  const [createActivity, { isLoading: isCreatingActivity }] = useCreateActivityMutation({ fixedCacheKey: 'create-activity-shared' });

  useEffect(() => {
    // Reset data on unmount
    return () => form.resetFields();
  }, []);

  const hasLiveChatPlugin = useCallback(() => {
    return (bot?.plugins || []).find(plugin => plugin.integration.category === INTEGRATION_CATEGORY.LIVECHAT);
  }, [bot]);

  const onSubmit = async (values) => {
    // Construct the submit values
    let valuesToSubmit = { ...values };

    if (valuesToSubmit) {
      valuesToSubmit = { ...valuesToSubmit, ...generateFlowGraphChaptersource(valuesToSubmit.name.trim(), values.type, primaryBotLanguage) };

      const isSaveSuccessful = await createActivity({ orgId, tenantId, data: valuesToSubmit });

      if (isSaveSuccessful) {
        // Trigger callback to notify parent component of success
        onSuccess(valuesToSubmit);
        form.resetFields();
      }
    }
  };

  return (
    <Row gutter={24}>
      <Col xs={24}>
        <Form
          name="activityCreateForm"
          layout="vertical"
          onFinish={onSubmit}
          form={form}
        >
          <Row gutter={16} className="mb-3">
            <Col xs={24}>
              <Form.Item
                name="name"
                label={t('Name')}
                rules={[{
                  required: true,
                  whitespace: true,
                  message: `${t('Please type the name of the flow.')}`
                }]}
              >
                <Input
                  placeholder={t('Type the name of the flow')}
                  size="large"
                />
              </Form.Item>
            </Col>

            <Col xs={24}>
              <Form.Item
                name="type"
                label={t('Type')}
                rules={[{
                  required: true,
                  message: `${t('Please select a type')}.`
                }]}
              >
                <Select
                  placeholder={t('Select a type')}
                  size="large"
                >
                  {
                    Object.entries(ACTIVITY_TYPES)
                      .filter(activity => activity[1] !== ACTIVITY_TYPES.AUTOMATED_ANSWERS && activity[1] !== ACTIVITY_TYPES.LIVECHAT_SYSTEM_MESSAGE && activity[1] !== ACTIVITY_TYPES.KNOWLEDGE_BASE_ARTICLE) // Hide Automated Answers, Livechat System Messages and Knowledge Base Article option (as it is no graph)
                      .filter(activity => !hasLiveChatPlugin() ? activity[1] !== ACTIVITY_TYPES.LIVECHAT_FLOW : true) // Hide Live-chat flow option if the bot has no live-chat enabled
                      .filter(activity => !IS_USER_FEEDBACK_ENABLED ? activity[1] !== ACTIVITY_TYPES.USER_FEEDBACK : true) // Hide User Feedback option if the feature flag is disabled
                      .map(([key, value]) => (
                        <Option key={key} value={value}>{t(value, { context: 'activity type' })}</Option>
                      ))
                  }
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row justify="end">
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              loading={isCreatingActivity}
            >
              {t('Create Flow')}
            </Button>
          </Row>
        </Form>
      </Col>
    </Row>
  );
};

ActivityForm.propTypes = {
  onSuccess: PropTypes.func
};

export default ActivityForm;
